import React, { useEffect, useState, useRef, useCallback } from "react";
import Header from "./Header";
import Footer from "./Footer";
import Footerbrand from "./Footerbrand";
import { Link } from "react-router-dom";
import Breadcrumb from "./Breadcrumb";
import { useDataContext } from "./DataContext";
import Development from "./assets/image/Digital-Marketing.webp";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

/* ---------------- SKELETON CARD ---------------- */
const BlogSkeleton = () => (
  <div className="col-lg-4 col-md-6 col-sm-6 mb-3 px-2">
    <div className="p-3 border rounded bg-white">
      <div className="skeleton-shimmer rounded-1 mb-3" style={{ height: "180px" }} />
      <div className="placeholder-glow">
        <span className="placeholder col-10 mb-2" style={{ height: "24px", opacity: 0.1 }}></span>
        <span className="placeholder col-12 mb-1" style={{ opacity: 0.1 }}></span>
        <span className="placeholder col-12 mb-1" style={{ opacity: 0.1 }}></span>
        <span className="placeholder col-8 mb-3" style={{ opacity: 0.1 }}></span>
        <span className="placeholder col-12" style={{ height: "40px", opacity: 0.1 }}></span>
      </div>
    </div>
  </div>
);

const Blog = () => {
  const {
    blogs: cachedBlogs,
    fetchBlogs,
    fetchMoreBlogs,
    hasMoreBlogs
  } = useDataContext();

  const [visibleBlogs, setVisibleBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  useEffect(() => {
    const originalAddEventListener = HTMLElement.prototype.addEventListener;

    HTMLElement.prototype.addEventListener = function (
      event,
      callback,
      options
    ) {
      if (event === "DOMNodeInserted") {
        return;
      }
      return originalAddEventListener.call(this, event, callback, options);
    };

    const loadBlogs = async () => {
      // If cached blogs exist, show ALL immediately (user already viewed them)
      if (cachedBlogs && cachedBlogs.length > 0) {
        setVisibleBlogs(cachedBlogs);
        setLoading(false);
        return;
      }

      // First time visit - fetch first batch from Firebase
      setLoading(true);
      const fetchedBlogs = await fetchBlogs();

      if (fetchedBlogs && fetchedBlogs.length > 0) {
        setVisibleBlogs(fetchedBlogs);
      }
      setLoading(false);
    };

    loadBlogs();

    return () => {
      HTMLElement.prototype.addEventListener = originalAddEventListener;
    };
  }, [cachedBlogs, fetchBlogs]);

  // Load more blogs when scrolling (fetches from Firebase)
  const loadMoreBlogs = useCallback(async () => {
    if (loadingMore || !hasMoreBlogs) return;

    const previousLength = visibleBlogs.length;
    setLoadingMore(true);
    const updatedBlogs = await fetchMoreBlogs();

    // Only update if we got new blogs
    if (updatedBlogs && updatedBlogs.length > previousLength) {
      setVisibleBlogs(updatedBlogs);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMoreBlogs, fetchMoreBlogs, visibleBlogs.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreBlogs && !loadingMore) {
          loadMoreBlogs();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreBlogs, loadingMore, loadMoreBlogs]);

  const truncateText = (text, length) => {
    return text.length > length ? text.slice(0, length) + "..." : text;
  };

  return (
    <div>
      <Header name="Blog" />

      <Breadcrumb
        title="Blog"
        highlight="& Best Advice"
        paths={[
          { name: "Home", link: "/Home" },
          { name: "Blog", link: null }
        ]}
      />

      <section className="mb-35 mt-35 px-3 p-md-0">
        <div className="container p-0">
          <div className="row">
            <div className="col-md-12">
              <div className="shadow-box mb-5 p-4 rounded-4 bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-7 col-md-12 mb-4 mb-lg-0">
                    <h2 className="fw-bold mb-3 text-dark">Business Development Advice</h2>
                    <p className="text-secondary lead fs-6 mb-0">
                      Conduct thorough market research to identify gaps in the
                      market, assess competition, and understand customer
                      preferences. Leverage data analytics to inform your business
                      development strategies. Monitor key performance indicators
                      (KPIs) and use data to refine your approach and identify
                      areas for improvement.
                    </p>
                  </div>
                  <div className="col-lg-5 col-md-12 text-center">
                    <div className="row justify-content-center">
                      <div className="col-12 col-md-9">
                        <img
                          src={Development}
                          className="img-fluid w-100"
                          loading="lazy"
                          alt="Business Development Advice"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Blog Cards */}
              <div className="row mt-15">
                {loading ? (
                  // Initial Skeleton Loader (3 cards)
                  Array.from({ length: 3 }).map((_, i) => (
                    <BlogSkeleton key={i} />
                  ))
                ) : (
                  <>
                    {visibleBlogs.map((blog) => (
                      <div key={blog.id} className="col-lg-4 col-md-6 col-sm-6 mb-3 px-2">
                        <div className="p-3 border rounded bg-white">
                          {blog.image && (
                            <img
                              src={blog.image}
                              className="rounded-1 mb-3 w-100"
                              alt={blog.title}
                              loading="lazy"
                            />
                          )}
                          <div>
                            <p className="fw-semibold text-dark fs-4">{blog.title}</p>
                            <ReactQuill
                              value={truncateText(
                                blog.content || "No content available",
                                100
                              )}
                              readOnly
                              theme="bubble"
                              className="mt-3 mb-4"
                            />
                            <Link
                              to={`/blog/${blog.id}`}
                              className="btn-main w-100 py-2 rounded-1 border-0"
                            >
                              Read More
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Infinite Scroll Loader - Only show when loading more */}
              {hasMoreBlogs && loadingMore && (
                <div ref={loaderRef} className="row mt-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <BlogSkeleton key={`loader-${i}`} />
                  ))}
                </div>
              )}

              {/* Hidden loader trigger for intersection observer */}
              {hasMoreBlogs && !loadingMore && (
                <div ref={loaderRef} style={{ height: "1px" }} />
              )}
            </div>
          </div>
        </div>
      </section>
      <Footerbrand />
      <Footer />
    </div>
  );
};

export default Blog;
